import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import App from './App'
import { applyAppearance, applyTheme } from '@/design/appearance'
import { requestPersistentStorage } from '@/core/storage/persist'
import { setupServiceWorker } from '@/pwa/registerSW'
import { getSettings } from '@/core/repository/settingsRepository'
import { seedIfEmpty } from '@/db/seed'
import { DEFAULT_SETTINGS } from '@/core/types/settings'
import { logger } from '@/core/logging/logger'

import './styles/themes.css'
import './styles/base.css'

// Apply the default theme SYNCHRONOUSLY, before React mounts, so there is no
// flash of the wrong colour scheme while the database opens. Only the two
// attributes that matter for that -- the rest is corrected below.
applyTheme(DEFAULT_SETTINGS.themeStyle, DEFAULT_SETTINGS.colorScheme)

// Belt-and-braces against storage eviction. Requested every launch because iOS
// may not grant it permanently.
void requestPersistentStorage()

// Create settings and the starter catalogue on first launch. Guarded three
// ways so a second device can never duplicate the catalogue -- see seed.ts.
void seedIfEmpty()
  .then(async (seeded) => {
    if (seeded) logger.info('db', 'First launch: catalogue seeded')
    const settings = await getSettings()
    // Now the full set: accent preset, colourblind textures, corner radius,
    // animation choice and speed.
    applyAppearance(settings)
  })
  .catch((err: unknown) => {
    logger.error('db', 'Startup failed', err)
  })

setupServiceWorker({
  onUpdateAvailable: (reload) => {
    // Phase 11 replaces this with a proper toast. Never auto-reload.
    logger.info('pwa', 'Update ready — reload to apply')
    if (window.confirm('A new version is available. Reload now?')) reload()
  },
})

const container = document.getElementById('app')
if (!container) throw new Error('Root element #app not found in index.html')

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
